// frontend/src/components/athleteEditor/AcademicScoresFields.jsx
// The four verifiable academic scores — weighted/unweighted GPA, ACT, SAT —
// as seal-bearing fields for the admin edit panel's Academics section.
//
// These live on AthleteProfile as flat columns with matching `<col>_verified`
// flags, so they need no new save brain: useMeasurableSaves already does
// exactly this (POST /athletes/profile for the value, POST
// /athletes/{userId}/verify for the seal, optimistic set + revert on error).
// Passing isProfileField=true routes the value to /athletes/profile rather
// than the catalog's /update-stat, which is correct — none of the four is a
// catalog measurable.
//
// Deliberately NOT a port of AcademicsPage.js's version of these fields. The
// athlete's page raises a confirmation when a verified score is edited,
// because an athlete cannot restore the blue dot they just cleared. An admin
// IS staff and can re-verify from the seal right here, so that dialog would be
// a false warning. The admin surface behaves like the measurables ledger
// instead: edit the value, toggle the seal.
import React from 'react';
import EditorField from './EditorField';
import SealButton from './SealButton';
import useMeasurableSaves from './useMeasurableSaves';

// step is what an <input type="number"> allows, not a validation rule — the
// backend coerces (gpa columns are Float, the test scores Integer).
const SCORES = [
  { key: 'gpa_weighted', label: 'GPA (Weighted)', step: '0.01' },
  { key: 'gpa_unweighted', label: 'GPA (Unweighted)', step: '0.01' },
  { key: 'act_score', label: 'ACT', step: '1' },
  { key: 'sat_score', label: 'SAT', step: '1' },
];

export default function AcademicScoresFields({
  userId, profile, perms, apiUrl, getConfig, logs, onChange, onSaved,
}) {
  const { values, setValues, errors, commitField, toggleVerify, fieldState, sealState, sealTitle } =
    useMeasurableSaves({ userId, profile, apiUrl, getConfig, logs, onChange, onSaved });

  const editable = !!(perms && perms.canEditValues);
  const canVerify = !!(perms && perms.canVerify);

  return (
    <div className="ae-grid5">
      {SCORES.map(({ key, label, step }) => (
        <EditorField key={key} label={label} state={fieldState(key)}>
          <input
            className="ae-field__input"
            type="number"
            step={step}
            // EditorField's <label> is decorative (no htmlFor), so the input
            // carries its own accessible name.
            aria-label={label}
            value={values[key] ?? ''}
            disabled={!editable}
            placeholder="—"
            onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
            onBlur={e => editable && commitField(key, e.target.value, true)}
          />
          {canVerify && (
            <SealButton state={sealState(key)} title={sealTitle(key)} onToggle={() => toggleVerify(key)} />
          )}
          {errors[key] && <div className="form-field__error">{errors[key]}</div>}
        </EditorField>
      ))}
    </div>
  );
}
